import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import { SparkleIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import { Link as RouterLink } from 'react-router-dom';

import { PATHS } from '../../paths';
import { useSidePanel } from '../../providers/SidePanelProvider';

const ASK_MANTLE_LABEL = 'Ask Mantle';

interface IMantleAssistantNavButtonBaseProps {
  /** See `IMantleAssistantNavButtonProps['variant']`. */
  variant: 'full' | 'compact';
  /** Whether the viewport is `xl+`, where the full pill toggles the docked panel. */
  isXl: boolean;
  /** Whether the assistant currently holds the side-panel column. */
  isAssistantShowing: boolean;
  onToggleAssistant: () => void;
}

/**
 * Presentational body of `MantleAssistantNavButton`, with the viewport and
 * side-panel state lifted into props so stories can render every state
 * synchronously. Resting look (mint fill, outer shadow) per Figma node
 * `6382:471350`; pressed look (warm fill, inner shadow) per the assistant
 * button inside Figma node `8006:877943`.
 */
export const MantleAssistantNavButtonBase = ({
  variant,
  isXl,
  isAssistantShowing,
  onToggleAssistant,
}: IMantleAssistantNavButtonBaseProps) => {
  if (variant === 'compact') {
    return (
      <RouterLink
        to={PATHS.ASSISTANT}
        aria-label={ASK_MANTLE_LABEL}
        className={cx(
          'bg-utility-mist-100 border-utility-mist-300 outline-focus-ring shadow-xs',
          'flex size-9 shrink-0 items-center justify-center rounded-lg border',
          'transition hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2'
        )}
      >
        <SparkleIcon aria-hidden="true" className="text-utility-brand-825 size-[18px] shrink-0" />
      </RouterLink>
    );
  }

  // Pressed only where the pill is a toggle: below `xl` it is a link, and
  // `visible` can be 'assistant' with no panel on screen (`HomeScene` owns the
  // breakpoint check, not the provider), so the state alone must not style it.
  const isPressed = isXl && isAssistantShowing;

  const pillClassName = cx(
    isPressed
      ? 'bg-navigation-active border-secondary shadow-nav-pressed'
      : 'bg-utility-mist-100 border-utility-mist-300 shadow-xs',
    'outline-focus-ring flex w-full items-center gap-2 rounded-xl border p-4',
    'cursor-pointer transition hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2'
  );

  const pillContent = (
    <>
      <SparkleIcon aria-hidden="true" className="text-utility-brand-825 size-[21px] shrink-0" />
      <span className="text-utility-brand-825 text-md font-semibold">{ASK_MANTLE_LABEL}</span>
    </>
  );

  // xl+: toggle the docked side panel. Below xl: navigate to the route.
  if (isXl) {
    return (
      <button
        type="button"
        onClick={onToggleAssistant}
        // Disclosure pattern: `aria-expanded` reflects whether the assistant is
        // the surface on screen. Not paired with `aria-pressed` — ARIA forbids
        // both on one control. No `aria-label` either: the visible label is the
        // accessible name, and overriding it would break voice control.
        aria-expanded={isAssistantShowing}
        className={pillClassName}
      >
        {pillContent}
      </button>
    );
  }

  return (
    <RouterLink to={PATHS.ASSISTANT} className={pillClassName}>
      {pillContent}
    </RouterLink>
  );
};

interface IMantleAssistantNavButtonProps {
  /**
   * `full` (default) renders the wide footer button with the sparkle plus
   * "Ask Mantle" label, used inside the desktop sidebar and the mobile
   * drawer. `compact` renders the 36px square icon-only variant used inside
   * the 64px tablet icon rail.
   */
  variant?: 'full' | 'compact';
}

/**
 * Footer entry-point to the assistant.
 * Visually distinct from the regular nav list — a filled, bordered button
 * carrying the sparkle and label, per Figma node `6382:471350` (resting) and
 * the assistant button inside node `8006:877943` (pressed, while the
 * assistant holds the panel). The caller (`FullSidebarContent` /
 * `IconRailSidebar` / `MobileNavDrawer`) is responsible for gating visibility
 * behind `feat-assistant`; this component renders unconditionally when
 * mounted.
 *
 * **Behavior depends on viewport.** At `xl+` the assistant docks as a side
 * panel in the shell, so the full pill is a `<button>` that toggles the
 * panel's visibility (with `aria-expanded` and the pressed styling reflecting
 * the open state). Below `xl` — and for the icon-rail compact variant, which
 * only renders at `md` — there is no panel, so it stays a `RouterLink` to the
 * full-page `/assistant` route.
 */
const MantleAssistantNavButton = ({ variant = 'full' }: IMantleAssistantNavButtonProps) => {
  const { visible, toggleAssistant } = useSidePanel();
  const isXl = useBreakpoint('xl');
  // The column also seats the Alerts Center, so "is the panel open" is not the
  // same question as "is the assistant showing" — this button speaks for the
  // assistant, and would otherwise claim to hide a panel it does not own.
  const isAssistantShowing = visible === 'assistant';

  return (
    <MantleAssistantNavButtonBase
      variant={variant}
      isXl={isXl}
      isAssistantShowing={isAssistantShowing}
      onToggleAssistant={toggleAssistant}
    />
  );
};

export default MantleAssistantNavButton;
